import { useTranslation } from "@equiem/web-ng-lib";
import { ErrorMessage, ProgressCircle, useShowError } from "@equiem/web-ng-lib";
import { NextRouter, useRouter } from "next/router";
import React, { useEffect } from "react";
import { Container } from "react-bootstrap";
import {
  BuildingInfoPostDocument,
  BuildingInfoPostQuery,
  BuildingInfoPostQueryVariables,
  DealDocument,
  DealQuery,
  DealQueryVariables,
  EventOccurrenceDocument,
  EventOccurrenceQuery,
  EventOccurrenceQueryVariables,
  NewsPostDocument,
  NewsPostQuery,
  NewsPostQueryVariables,
  useContentLookupQuery,
  VendorProductsDocument,
  VendorProductsQuery,
  VendorProductsQueryVariables,
} from "../generated/gateway-client";

interface Props {
  uuid: string;
  ns?: string;
  nx?: string;
  /** ISO 8601 date-only (`YYYY-MM-DD`) day, forwarded to bookable products. */
  date?: string;
}

/**
 * Query string fragments carrying `date` to the booking page.
 *
 * It has to go on the href as well as the `as` URL: Next builds `router.query`
 * from the href and only merges the dynamic path segments out of `as`, so a
 * param that appears solely in `as` reaches the address bar but never the page.
 */
const bookingDateQuery = (date?: string): { href: string; as: string } => {
  if (date == null) {
    return { href: "", as: "" };
  }

  const param = `date=${encodeURIComponent(date)}`;

  return { href: `?${param}`, as: `&${param}` };
};

const useContentLookupRedirect = ({
  uuid,
  ns,
  nx,
  date,
}: Props): {
  loading: boolean;
  redirect?: Parameters<NextRouter["replace"]>;
  error?: string;
} => {
  const { t } = useTranslation();

  const { loading, client, data } = useContentLookupQuery({
    variables: { uuid },
  });

  if (loading) {
    return { loading };
  }

  if (data == null) {
    throw new Error(t("main.failedToLoadContentError"));
  }

  const { contentLookup } = data;

  switch (contentLookup?.__typename) {
    case "NewsEdge": {
      const { node } = contentLookup;
      if (node == null) {
        return { loading: false, error: t("main.failedToLoadNewsPostError") };
      }

      client.writeQuery<NewsPostQuery, NewsPostQueryVariables>({
        query: NewsPostDocument,
        data: { newsPost: node },
        variables: { uuid: node.uuid },
      });

      return {
        loading: false,
        redirect: ["/news/[uuid]", `/news/${node.uuid}?ns=${ns}&nx=${nx}`],
      };
    }

    case "EventOccurrenceEdge": {
      const { node } = contentLookup;
      if (node == null) {
        return { loading: false, error: t("main.failedToLoadEventPostError") };
      }

      client.writeQuery<EventOccurrenceQuery, EventOccurrenceQueryVariables>({
        query: EventOccurrenceDocument,
        data: { eventOccurrence: node },
        variables: { uuid: node.uuid },
      });

      return {
        loading: false,
        redirect: ["/event/[uuid]", `/event/${node.uuid}?ns=${ns}&nx=${nx}`],
      };
    }

    case "BuildingInfoEdge": {
      const { node } = contentLookup;
      if (node == null) {
        return {
          loading: false,
          error: t("main.failedToLoadBuildingInfoPostError"),
        };
      }

      client.writeQuery<BuildingInfoPostQuery, BuildingInfoPostQueryVariables>({
        query: BuildingInfoPostDocument,
        data: { buildingInfoPost: node },
        variables: { uuid: node.uuid },
      });

      return {
        loading: false,
        redirect: ["/info/[uuid]", `/info/${node.uuid}?ns=${ns}&nx=${nx}`],
      };
    }

    case "BookableProductContentEdge": {
      const { node } = contentLookup;

      if (node == null) {
        return {
          loading: false,
          error: t("main.failedToLoadBookableProductError"),
        };
      }

      // Only the booking page understands `date`, and it is omitted when absent
      // rather than forwarded as the literal string "undefined".
      const dateQuery = bookingDateQuery(date);

      return {
        loading: false,
        redirect: [
          `/store/booking/[uuid]${dateQuery.href}`,
          `/store/booking/${node.uuid}?ns=${ns}&nx=${nx}${dateQuery.as}`,
        ],
      };
    }

    case "SubscriptionProductContentEdge":
    case "PlainProductContentEdge": {
      const { node } = contentLookup;
      if (node == null) {
        return { loading: false, error: t("main.failedToLoadProductError") };
      }

      return {
        loading: false,
        redirect: [
          "/store/product/[uuid]",
          `/store/product/${node.uuid}?ns=${ns}&nx=${nx}`,
        ],
      };
    }

    case "VendorContentEdge":
    case "DealVendorContentEdge": {
      const { node } = contentLookup;

      if (node == null) {
        return { loading: false, error: t("main.failedToLoadVendorError") };
      }

      client.writeQuery<VendorProductsQuery, VendorProductsQueryVariables>({
        query: VendorProductsDocument,
        data: { vendorUnion: node },
        variables: { uuid: node.uuid },
      });

      return {
        loading: false,
        redirect: [
          "/store/vendor/[uuid]",
          `/store/vendor/${node.uuid}?ns=${ns}&nx=${nx}`,
        ],
      };
    }

    case "DealContentEdge": {
      const { node } = contentLookup;

      if (node == null) {
        return { loading: false, error: t("main.failedToLoadDealError") };
      }

      client.writeQuery<DealQuery, DealQueryVariables>({
        query: DealDocument,
        data: { deal: node },
        variables: { uuid: node.uuid },
      });

      return {
        loading: false,
        redirect: [
          "/store/deal/[uuid]",
          `/store/deal/${node.uuid}?ns=${ns}&nx=${nx}`,
        ],
      };
    }

    case undefined:
    case null: {
      return { loading: false, error: t("main.contentCouldNotBeFoundError") };
    }

    default: {
      return {
        loading: false,
        error: `${t("main.unsupportedContentTypeError")}: "${contentLookup?.__typename}".`,
      };
    }
  }
};

export const ContentRedirect: React.FC<Props> = ({ uuid, ns, nx, date }) => {
  const { t } = useTranslation();

  const router = useRouter();
  const showError = useShowError();
  const { loading, redirect, error } = useContentLookupRedirect({
    uuid,
    ns,
    nx,
    date,
  });

  useEffect(() => {
    if (redirect != null) {
      router.replace(...redirect).catch(showError);
    }
  }, [redirect]);

  return !loading && error == null ? null : (
    <Container className="my-5 text-center">
      {loading ? (
        <ProgressCircle size="lg" />
      ) : (
        <ErrorMessage>{error ?? t("main.notFoundError")}</ErrorMessage>
      )}
    </Container>
  );
};
